import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Phone, MessageSquare, UserPlus, Send, RefreshCw, PhoneIncoming, PhoneOutgoing, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const QuoTab = () => {
  const [calls, setCalls] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsTo, setSmsTo] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [sending, setSending] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/command-center/quo/calls?limit=25`).then(r => r.json()),
      fetch(`${API}/api/command-center/quo/messages?limit=25`).then(r => r.json()),
    ]).then(([c, m]) => {
      setCalls(c.calls || []);
      setMessages(m.messages || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sendSms = async () => {
    if (!smsTo || !smsBody) return toast.error('Phone number and message required');
    setSending(true);
    try {
      const r = await fetch(`${API}/api/command-center/quo/send-sms`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: smsTo, message: smsBody }),
      });
      const d = await r.json();
      if (d.success) { toast.success('SMS sent!'); setSmsTo(''); setSmsBody(''); load(); }
      else { toast.error('SMS failed — check A2P registration in Quo dashboard'); }
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  const syncContact = async () => {
    if (!contactName && !contactPhone) return toast.error('Name or phone required');
    try {
      const r = await fetch(`${API}/api/command-center/quo/sync-contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: contactName, phone: contactPhone, email: contactEmail }),
      });
      const d = await r.json();
      if (d.success) { toast.success(`Contact synced: ${d.contact_id}`); setContactName(''); setContactPhone(''); setContactEmail(''); }
      else { toast.error('Sync failed'); }
    } catch { toast.error('Failed to sync contact'); }
  };

  return (
    <div className="space-y-6" data-testid="quo-tab">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Recent Calls</p>
          <p className="text-2xl font-bold text-blue-900">{calls.length}</p>
        </Card>
        <Card className="p-4 bg-green-50 border-green-200">
          <p className="text-[10px] font-bold text-green-600 uppercase tracking-wider">Recent Texts</p>
          <p className="text-2xl font-bold text-green-900">{messages.length}</p>
        </Card>
        <Card className="p-4 bg-gray-50 border-gray-200">
          <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Quo Number</p>
          <p className="text-lg font-bold text-gray-900">(678) 846-3009</p>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Send SMS */}
        <Card className="p-5 border-gray-200">
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2"><Send className="h-4 w-4 text-blue-600" /> Send SMS</h3>
          <div className="space-y-2">
            <input value={smsTo} onChange={e => setSmsTo(e.target.value)} placeholder="Phone (+1XXXXXXXXXX)" className="w-full text-xs border rounded-lg px-3 py-2" data-testid="sms-to" />
            <textarea value={smsBody} onChange={e => setSmsBody(e.target.value)} placeholder="Message..." rows={3} className="w-full text-xs border rounded-lg px-3 py-2 resize-none" data-testid="sms-body" />
            <Button onClick={sendSms} disabled={sending} className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs h-8" data-testid="sms-send-btn">
              {sending ? 'Sending...' : 'Send SMS'}
            </Button>
          </div>
        </Card>

        {/* Sync Contact */}
        <Card className="p-5 border-gray-200">
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2"><UserPlus className="h-4 w-4 text-green-600" /> Sync Contact to Quo</h3>
          <div className="space-y-2">
            <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Full Name" className="w-full text-xs border rounded-lg px-3 py-2" data-testid="contact-name" />
            <input value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="Phone (+1XXXXXXXXXX)" className="w-full text-xs border rounded-lg px-3 py-2" data-testid="contact-phone" />
            <input value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="Email (optional)" className="w-full text-xs border rounded-lg px-3 py-2" data-testid="contact-email" />
            <Button onClick={syncContact} className="w-full bg-green-600 hover:bg-green-700 text-white text-xs h-8" data-testid="contact-sync-btn">
              Create Contact in Quo
            </Button>
          </div>
        </Card>
      </div>

      {/* Call & Message Log */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-2"><Phone className="h-4 w-4 text-orange-600" /> Call Log</h3>
            <button onClick={load} className="p-1.5 rounded hover:bg-gray-100"><RefreshCw className="h-3.5 w-3.5 text-gray-500" /></button>
          </div>
          {loading ? (
            <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
          ) : calls.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No recent calls</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {calls.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 text-xs">
                  <div className="flex items-center gap-2">
                    {c.direction === 'incoming' ? <PhoneIncoming className="h-3.5 w-3.5 text-green-600" /> : <PhoneOutgoing className="h-3.5 w-3.5 text-blue-600" />}
                    <span className="font-semibold">{c.from || 'Unknown'}</span>
                    <ArrowRight className="h-3 w-3 text-gray-400" />
                    <span>{c.to || 'Unknown'}</span>
                  </div>
                  <span className="text-gray-400">{c.duration || 0}s</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-purple-600" /> Message Log</h3>
            <button onClick={load} className="p-1.5 rounded hover:bg-gray-100"><RefreshCw className="h-3.5 w-3.5 text-gray-500" /></button>
          </div>
          {loading ? (
            <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
          ) : messages.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No recent messages</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {messages.map((m, i) => (
                <div key={i} className="py-2 px-3 rounded-lg bg-gray-50 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-semibold ${m.direction === 'incoming' ? 'text-green-700' : 'text-blue-700'}`}>
                      {m.direction === 'incoming' ? 'From' : 'To'}: {m.direction === 'incoming' ? m.from : (m.to?.[0] || 'Unknown')}
                    </span>
                    <span className="text-gray-400">{m.createdAt ? new Date(m.createdAt).toLocaleDateString() : ''}</span>
                  </div>
                  <p className="text-gray-600 truncate">{m.body || m.content || '(no content)'}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* A2P Notice */}
      <Card className="p-4 bg-amber-50 border-amber-200">
        <p className="text-xs text-amber-800"><strong>Note:</strong> SMS sending requires US Carrier Registration (A2P) in your Quo dashboard. Contact sync and call/message logging work immediately. Visit <a href="https://support.quo.com" className="underline" target="_blank" rel="noreferrer">Quo Support</a> to complete A2P registration.</p>
      </Card>
    </div>
  );
};

export default QuoTab;
